import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { checkAndApplyPendingDiscountCode } from './auto-apply-coupon.js';
import { DeliveryEvents } from './delivery-core.js';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);
  }

  #handleCartAdd = async () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.handleCartButtonClick(true);
    }
    
    // Check for and apply any pending discount codes when cart becomes available
    await checkAndApplyPendingDiscountCode();
  };

  /**
   * Handles postal code change event to update checkout links
   * @param {CustomEvent} event - The postal code changed event
   */
  #handlePostalCodeChanged = (event) => {
    try {
      const { postalCode } = event.detail;
      
      // Use shared utility to update checkout links in the cart drawer
      /** @type {any} */ (window).CheckoutLinkUpdater.updateCheckoutLinks(postalCode, this);
      
      // Also update checkout links in the cart summary (outside the drawer)
      /** @type {any} */ (window).CheckoutLinkUpdater.updateCheckoutLinks(postalCode, document);

    } catch (error) {
      console.error('❌ [CART-DRAWER] Error handling postal code change:', error);
    }
  };

  open() {
    this.showDialog();
    this.handleCartButtonClick(true);
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.handleCartButtonClick(false);
  }

  openDeliveryDrawer() {
    // Close the cart drawer first
    this.close();
    
    // Use the centralized delivery times drawer opening function
    if (/** @type {any} */ (window).combinedDrawers?.openDelivery) {
      /** @type {any} */ (window).combinedDrawers.openDelivery();
    } else {
      // Fallback to direct access
      const deliveryDrawer = document.querySelector('delivery-times-drawer-component');
      if (deliveryDrawer) {
        /** @type {any} */ (deliveryDrawer).open();
      }
    }
  }

  /**
   * @param {boolean} isOpen
   */
  handleCartButtonClick(isOpen) {
    const collectionCartButton = document.querySelectorAll('.collection-cart-button');
    if (collectionCartButton.length > 0) {
      for (const button of collectionCartButton) {
        button.classList.toggle('active', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
      }
    }
  }

  /**
   * Handles next button click using global utility
   * @param {Event} event - The click event
   */
  handleNextButton = async (event) => {
    // Find the actual instance of the custom collection footer component in the DOM
    const footerComponent = document.querySelector('custom-collection-footer-component');
    
    if (footerComponent && 'handleCartNextButton' in footerComponent && typeof /** @type {any} */ (footerComponent).handleCartNextButton === 'function') {
      await /** @type {any} */ (footerComponent).handleCartNextButton(event);
    } else {
      const target = /** @type {HTMLElement | null} */ (event?.target ?? null);
      const checkoutUrl = target?.getAttribute('data-checkout-url');
      const weekmenuUrl = target?.getAttribute('data-weekmenu-url');
      if (window.location.pathname.includes('collection')) {
        if (weekmenuUrl) {
          window.location.href = weekmenuUrl;
        }
      } else {
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
      }
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
